import { create } from 'zustand';
import axiosClient from '../services/api';

const useAnalyticsStore = create((set, get) => ({
  progressOverview: null,
  courseProgress: [],
  nextSteps: null,
  recommendations: [],
  schedule: [],
  personalEvents: [],
  badges: [],
  leaderboard: [],
  analyticsOverview: null,
  coursePerformance: [],
  engagementData: null,
  studentDemographics: null,
  geographicDistribution: null,
  cohortAnalysis: null,
  instructorOverview: null,
  dailyMetrics: null,
  platformAnalytics: null,
  growthData: [],
  systemHealth: null,
  isLoading: false,
  error: null,

  // Student actions
  fetchProgressOverview: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/analytics/progress/overview');
      set({
        progressOverview: response.data.data,
        isLoading: false
      });
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch progress overview';
      set({
        error: errorMessage,
        isLoading: false
      });
      return null;
    }
  },

  fetchCourseProgress: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/analytics/progress/courses');
      set({
        courseProgress: response.data.data,
        isLoading: false
      });
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch course progress';
      set({
        error: errorMessage,
        isLoading: false
      });
      return null;
    }
  },

  fetchNextSteps: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/analytics/progress/next-steps');
      set({
        nextSteps: response.data.data,
        isLoading: false
      });
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch next steps';
      set({
        error: errorMessage,
        isLoading: false
      });
      return null;
    }
  },

  fetchBadges: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/badges/');
      set({ badges: response.data.badges, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch badges', isLoading: false });
    }
  },

  fetchLeaderboard: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/badges/leaderboard');
      set({ leaderboard: response.data.leaderboard, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch leaderboard', isLoading: false });
    }
  },

  fetchSchedule: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/analytics/schedule');
      set({
        schedule: response.data.data,
        isLoading: false
      });
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch schedule';
      set({
        error: errorMessage,
        isLoading: false
      });
      return null;
    }
  },

  createPersonalEvent: async (eventData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.post('/analytics/schedule/events', eventData);
      set({ isLoading: false });
      // Refresh schedule
      get().fetchSchedule();
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to create personal event';
      set({
        error: errorMessage,
        isLoading: false
      });
      return null;
    }
  },

  // Instructor actions
  fetchInstructorOverview: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/analytics/analytics/instructor/overview');
      set({ instructorOverview: response.data.data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch instructor overview', isLoading: false });
    }
  },

  fetchCoursePerformance: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/analytics/analytics/instructor/courses');
      set({ coursePerformance: response.data.courses, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch course performance', isLoading: false });
    }
  },

  fetchDailyMetrics: async (startDate, endDate) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/analytics/analytics/instructor/daily-metrics', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      });
      set({ dailyMetrics: response.data.data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch daily metrics', isLoading: false });
    }
  },

  fetchEngagementData: async (startDate, endDate) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/analytics/analytics/instructor/engagement', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      });
      set({ engagementData: response.data.data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch engagement data', isLoading: false });
    }
  },

  fetchStudentDemographics: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/analytics/analytics/instructor/demographics');
      set({ studentDemographics: response.data.demographics, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch student demographics', isLoading: false });
    }
  },

  fetchGeographicDistribution: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/analytics/analytics/instructor/geography');
      set({ geographicDistribution: response.data.formattedData, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch geographic distribution', isLoading: false });
    }
  },

  fetchCohortAnalysis: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/analytics/analytics/instructor/cohorts');
      set({ cohortAnalysis: response.data.cohortAnalysis, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch cohort analysis', isLoading: false });
    }
  },

  // Admin actions
  fetchPlatformAnalytics: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/analytics/analytics/admin/platform');
      set({
        platformAnalytics: response.data.data,
        isLoading: false
      });
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch platform analytics';
      set({
        error: errorMessage,
        isLoading: false
      });
      return null;
    }
  },

  fetchGrowthData: async (period = '30d') => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get(`/analytics/analytics/admin/growth?period=${period}`);
      set({
        growthData: response.data.data,
        isLoading: false
      });
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch growth data';
      set({
        error: errorMessage,
        isLoading: false
      });
      return null;
    }
  },

  fetchSystemHealth: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/analytics/analytics/admin/health');
      set({
        systemHealth: response.data.data,
        isLoading: false
      });
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch system health';
      set({
        error: errorMessage,
        isLoading: false
      });
      return null;
    }
  }
}));

export default useAnalyticsStore;